import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc, collectionGroup, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../context/Authcontext/AuthContext";
import useStore from "../store/useStore";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  ShieldCheck,
  Calendar,
  TrendingUp,
  CheckCircle,
  Bell,
  Wrench,
} from "lucide-react";
import { motion } from "framer-motion";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkRoleAndNavigate = async () => {
      // 1. Wait until Firebase Auth finishes initializing
      if (authLoading) {
        return;
      }

      // 2. If user is authenticated, determine their role & setup status
      if (currentUser) {
        try {
          const userRef = doc(db, "vendorUsers", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.userRole === "vendor" || !userData.userRole) {
              useStore.getState().setSessionContext({ vendorId: currentUser.uid, userRole: "vendor" });
              if (!userData.accountSetupCompleted) {
                navigate("/account-setup", { replace: true });
                return;
              }
              navigate("/dashboard", { replace: true });
              return;
            }
          }
          const staffQuery = query(collectionGroup(db, "staff"), where("email", "==", currentUser.email));
          const staffSnapshot = await getDocs(staffQuery);
          if (!staffSnapshot.empty) {
            const staffDoc = staffSnapshot.docs[0];
            const pathSegments = staffDoc.ref.path.split("/");
            if (pathSegments.length >= 5) {
              useStore.getState().setSessionContext({
                vendorId: pathSegments[1],
                locationId: pathSegments[3],
                staffId: staffDoc.id,
                userRole: staffDoc.data().role || "manager",
              });
              navigate("/dashboard", { replace: true });
              return;
            }
          }
          await signOut(auth);
          useStore.getState().clearSessionContext();
          if (isMounted) {
            setError("Access denied. No active vendor or staff account found.");
            setCheckingAuth(false);
          }
        } catch (err) {
          console.error("Error checking user role:", err);
          await signOut(auth);
          useStore.getState().clearSessionContext();
          if (isMounted) {
            setError("An error occurred. Please sign in again.");
            setCheckingAuth(false);
          }
        }
      } else {
        // User is not signed in -> safely render login form
        if (isMounted) {
          setCheckingAuth(false);
        }
      }
    };

    checkRoleAndNavigate();

    return () => {
      isMounted = false;
    };
  }, [currentUser, authLoading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.email || !formData.password) {
      setError("Please fill in both fields.");
      return;
    }
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, formData.email.trim(), formData.password);
      const user = userCredential.user;
      const userRef = doc(db, "vendorUsers", user.uid);
      const userSnap = await getDoc(userRef);

      let isVendor = false;
      let isStaff = false;
      let sessionData = {};

      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.userRole === "vendor" || !userData.userRole) {
          isVendor = true;
          sessionData = { vendorId: user.uid, userRole: "vendor" };
          if (!userData.accountSetupCompleted) {
            useStore.getState().setSessionContext(sessionData);
            navigate("/account-setup", { replace: true });
            return;
          }
        }
      }
      if (!isVendor) {
        const staffQuery = query(collectionGroup(db, "staff"), where("email", "==", formData.email.trim()));
        const staffSnapshot = await getDocs(staffQuery);
        if (!staffSnapshot.empty) {
          isStaff = true;
          const staffDoc = staffSnapshot.docs[0];
          const pathSegments = staffDoc.ref.path.split("/");
          if (pathSegments.length >= 5) {
            sessionData = {
              vendorId: pathSegments[1],
              locationId: pathSegments[3],
              staffId: staffDoc.id,
              userRole: staffDoc.data().role || "manager",
            };
          }
        }
      }
      if (!isVendor && !isStaff) {
        await signOut(auth);
        setError("Access denied. No active vendor or staff account found.");
        return;
      }
      useStore.getState().setSessionContext(sessionData);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      if (auth.currentUser) {
        await signOut(auth);
        useStore.getState().clearSessionContext();
      }
      let msg = "Failed to sign in. Please try again.";
      switch (error.code) {
        case "auth/wrong-password": msg = "Incorrect password."; break;
        case "auth/invalid-email": msg = "Invalid email format."; break;
        case "auth/invalid-credential": msg = "Invalid email or password."; break;
        case "auth/user-not-found": msg = "No account found with this email."; break;
        default: msg = error.message || msg;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="h-screen w-screen flex flex-col justify-center items-center bg-[#F8FAFC] relative overflow-hidden">
        {/* Soft background glows */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-blue-100/60 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col items-center text-center px-6 relative z-10"
        >
          {/* Animated Logo Card */}
          <motion.div
            animate={{ 
              scale: [1, 1.03, 1],
              opacity: [0.95, 1, 0.95]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="p-5 bg-white rounded-3xl shadow-[0_12px_40px_rgba(0,122,255,0.08)] border border-slate-100 mb-6"
          >
            <img
              src="/logo.png"
              alt="Americar"
              className="h-10 w-auto object-contain"
            />
          </motion.div>

          {/* Modern Spinner & Status */}
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-4 h-4 border-2 border-[#007AFF]/25 border-t-[#007AFF] rounded-full animate-spin" />
            <span className="text-sm font-bold text-slate-800 tracking-tight">
              Verifying your session...
            </span>
          </div>

          <p className="text-xs text-slate-400 font-medium">
            Checking credentials and opening your portal.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-white">

      {/* ── LEFT: Brand Panel with Animated Cards ── */}
      <div className="hidden lg:flex w-[45%] h-full flex-col justify-between relative overflow-hidden bg-[#EEF5FF] px-14 py-14">

        {/* Soft background glows */}
        <div className="absolute top-[-80px] right-[-80px] w-[360px] h-[360px] rounded-full bg-[#007AFF]/10 blur-[90px] pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[280px] h-[280px] rounded-full bg-indigo-200/40 blur-[70px] pointer-events-none" />

        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.3] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #93c5fd 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/">
            <img
              src="/logo.png"
              alt="Americar"
              className="h-9 w-auto object-contain"
            />
          </Link>
        </div>

        {/* ── Animated Cards Composition ── */}
        <div className="relative z-10 flex flex-col items-center justify-center gap-3 flex-1 py-10">

          {/* Card 1 — New Booking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.1 }, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 } }}
            className="w-full max-w-[300px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <Bell className="w-4.5 h-4.5 text-[#007AFF]" style={{ width: 18, height: 18 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800 truncate">New Booking</p>
              <p className="text-[11px] text-slate-400 truncate">Toyota Camry · Oil Change · 10:30 AM</p>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              Live
            </span>
          </motion.div>

          {/* Card 2 — Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.25 }, y: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 } }}
            className="w-full max-w-[300px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 self-end"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">This Week</p>
                <p className="text-lg font-bold text-slate-900 mt-0.5">$4,280</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="text-emerald-500" style={{ width: 16, height: 16 }} />
              </div>
            </div>
            {/* Mini bar chart */}
            <div className="flex items-end gap-1 h-8">
              {[40, 65, 45, 80, 55, 90, 72].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.07, ease: "easeOut" }}
                  style={{ height: `${h}%`, transformOrigin: "bottom" }}
                  className={`flex-1 rounded-sm ${i === 5 ? "bg-[#007AFF]" : "bg-blue-100"}`}
                />
              ))}
            </div>
            <p className="text-[10px] text-emerald-600 font-semibold mt-2">↑ 18% vs last week</p>
          </motion.div>

          {/* Card 3 — Service Complete */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.4 }, y: { duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 1.6 } }}
            className="w-full max-w-[300px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <CheckCircle className="text-emerald-500" style={{ width: 18, height: 18 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800">Service Complete</p>
              <p className="text-[11px] text-slate-400 truncate">Honda Accord · Brake Service</p>
            </div>
            <span className="text-[10px] font-semibold text-slate-400 shrink-0">Just now</span>
          </motion.div>

          {/* Card 4 — Staff Alert (small, offset) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.55 }, y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2.2 } }}
            className="w-full max-w-[260px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-3.5 flex items-center gap-3 self-start"
          >
            <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <Wrench className="text-violet-500" style={{ width: 14, height: 14 }} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-slate-800">3 jobs queued</p>
              <p className="text-[10px] text-slate-400">Bay 2 · Ready for dispatch</p>
            </div>
          </motion.div>

        </div>

        {/* Bottom text */}
        <div className="relative z-10">
          <span className="inline-block text-[#007AFF] text-[10px] font-bold tracking-[0.15em] uppercase mb-3 bg-white/70 border border-blue-100 px-2.5 py-1 rounded-full backdrop-blur-sm">
            Vendor Portal
          </span>
          <h1 className="text-slate-800 text-2xl font-bold tracking-tight leading-[1.3]">
            Your shop, supercharged.
          </h1>
          <p className="text-slate-400 text-sm mt-1.5">Everything you need, in one place.</p>
        </div>

      </div>

      {/* ── RIGHT: Login Form ── */}
      <div className="flex-1 h-full flex flex-col justify-center items-center px-8 sm:px-16 lg:px-20 xl:px-28 bg-white overflow-y-auto">

        {/* Mobile logo */}
        <div className="lg:hidden mb-10 self-start">
          <Link to="/">
            <img
              src="/logo.png"
              alt="Americar"
              className="h-9 w-auto object-contain"
            />
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          {/* Heading */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-1.5">Sign in</h2>
            <p className="text-slate-400 text-sm">Welcome back to your business portal.</p>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl mb-7 text-sm font-medium flex items-start gap-3"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
              <span>{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">

            {/* Email */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
                Business Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Mail className="w-4 h-4" />
                </div>
                <input
                  type="email"
                  placeholder="name@business.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF] transition-all font-medium text-slate-900 placeholder:text-slate-400"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-[#007AFF] font-semibold hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Lock className="w-4 h-4" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-11 pr-12 py-3.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF] transition-all font-medium text-slate-900 placeholder:text-slate-400"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-6 bg-[#007AFF] hover:bg-blue-600 active:scale-[0.99] text-white rounded-xl font-bold text-sm shadow-md shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In to Portal</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-slate-400 font-medium text-xs">
              <ShieldCheck className="w-4 h-4 text-emerald-500" /> 256-bit encrypted
            </span>
            <Link to="/signup" className="text-[#007AFF] font-bold text-sm hover:underline">
              New vendor? Sign up →
            </Link>
          </div>

        </motion.div>
      </div>

    </div>
  );
};

export default Login;
